import { z } from "zod"

import { scale } from "@productlane/lib/src/browser"
import { Form, Switch, useZodForm } from "@productlane/ui"

import type { Gate } from "./portal-override-sections"
import type {
	ThemeColorField,
	ThemeColors,
} from "@/screens/settings/settings-pages/theme-fields"
import { UpgradeButton } from "@/components/upgrade-button"
import { useRequiredPlans } from "@/lib/plans"
import {
	DarkModeField,
	EmbedImageField,
	ThemeColorFields,
	useThemeColorAutosave,
} from "@/screens/settings/settings-pages/theme-fields"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"

export interface PortalThemeValues {
	darkMode: boolean | null
	accentColor: string | null
	buttonTextColor: string | null
	darkModeAccentColor: string | null
	darkModeButtonTextColor: string | null
	backgroundColor: string | null
	darkModeBackgroundColor: string | null
	portalEmbedImageUrl: string | null
}

export function PortalThemeSection({
	values,
	fallback,
	onSave,
	gate,
}: {
	values: PortalThemeValues
	/** The workspace colors, shown where the portal has none of its own. */
	fallback: ThemeColors
	onSave: (args: Partial<PortalThemeValues>) => Promise<void>
	gate: Gate
}) {
	const { passes: hasRequiredPlan } = useRequiredPlans(scale.andUp)

	return (
		<>
			<SettingsSection
				title="Theme"
				description="Customize the theme of your widget and portal."
				className="mb-3"
			/>
			<SettingsContainer className="flex flex-col gap-4 divide-y-0">
				{/* The embed image applies with or without the switch, like the portal logo. */}
				<div className="flex flex-col gap-2">
					<EmbedImageField
						imageUrl={values.portalEmbedImageUrl}
						onUploaded={(portalEmbedImageUrl) =>
							onSave({ portalEmbedImageUrl })
						}
						onCleared={() => onSave({ portalEmbedImageUrl: null })}
					/>
				</div>

				<SettingItem
					title="Own theme"
					description="Use the colors below instead of the main portal's."
					action={
						hasRequiredPlan ? (
							<Switch
								name="overridesTheme"
								label={null}
								className="pr-1"
								checked={gate.checked}
								onCheckedChange={gate.onChange}
							/>
						) : (
							<UpgradeButton required={scale.andUp} />
						)
					}
				/>

				{gate.checked && (
					<PortalThemeFields
						values={values}
						fallback={fallback}
						onSave={onSave}
					/>
				)}
			</SettingsContainer>
		</>
	)
}

const PortalThemeColorsSchema = z.object({
	accentColor: z.string(),
	buttonTextColor: z.string(),
	darkModeAccentColor: z.string(),
	darkModeButtonTextColor: z.string(),
})

function PortalThemeFields({
	values,
	fallback,
	onSave,
}: {
	values: PortalThemeValues
	fallback: ThemeColors
	onSave: (args: Partial<PortalThemeValues>) => Promise<void>
}) {
	const form = useZodForm({
		schema: PortalThemeColorsSchema,
		defaultValues: {
			accentColor: values.accentColor ?? fallback.accentColor,
			buttonTextColor: values.buttonTextColor ?? fallback.buttonTextColor,
			darkModeAccentColor:
				values.darkModeAccentColor ?? fallback.darkModeAccentColor,
			darkModeButtonTextColor:
				values.darkModeButtonTextColor ?? fallback.darkModeButtonTextColor,
		},
	})
	const colors = form.watch()
	const persist = (field: ThemeColorField, value: string) =>
		void onSave({ [field]: value })

	useThemeColorAutosave({
		colors,
		isDirty: (field) => !!form.formState.dirtyFields[field],
		persist,
	})

	return (
		<Form
			className="flex flex-col gap-4"
			form={form}
			onSubmit={() => undefined}
		>
			<DarkModeField
				value={values.darkMode}
				onChange={(darkMode) => void onSave({ darkMode })}
			/>
			<ThemeColorFields
				colors={colors}
				onChange={(field, hex) =>
					form.setValue(field, hex, { shouldDirty: true })
				}
				onCommit={(field) => persist(field, colors[field])}
				backgrounds={{
					portalBackgroundColor: values.backgroundColor,
					portalDarkModeBackgroundColor: values.darkModeBackgroundColor,
				}}
				onBackgroundSave={(field, value) =>
					void onSave(
						field === "portalBackgroundColor"
							? { backgroundColor: value }
							: { darkModeBackgroundColor: value },
					)
				}
			/>
		</Form>
	)
}
